import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { subscribe, unsubscribe } from "../services/loadingServices/loading.service";

/**
 * Thin top progress bar (NProgress-style) for navigation/data feedback.
 *
 * Design notes (local prototype):
 * - Feedback, not decoration: a hairline that says "I heard your click" the
 *   instant navigation starts — not a page animation (which would feel slow
 *   on a tool navigated dozens of times a day).
 * - Appearance is DELAYED ~180ms: if a navigation/fetch finishes faster than
 *   that, the bar never shows, so quick loads don't flash a flicker.
 * - Driven by the app's global "loading" event (accurate completion) with a
 *   route-change trigger + safety timeout as a fallback.
 */
const SHOW_DELAY = 180;
const ACCENT = "#0F7679";

const RouteProgressBar: React.FC = () => {
  const location = useLocation();
  const [progress, setProgress] = useState(0); // 0 = hidden
  const active = useRef(false);
  const showTimer = useRef<ReturnType<typeof setTimeout>>();
  const trickle = useRef<ReturnType<typeof setInterval>>();
  const fade = useRef<ReturnType<typeof setTimeout>>();
  const safety = useRef<ReturnType<typeof setTimeout>>();

  const begin = () => {
    if (active.current || showTimer.current) return;
    clearTimeout(fade.current);
    showTimer.current = setTimeout(() => {
      showTimer.current = undefined;
      active.current = true;
      setProgress(8);
      trickle.current = setInterval(() => {
        setProgress((p) =>
          p <= 0 || p >= 90 ? p : Math.min(p + (p < 50 ? 7 : p < 75 ? 3 : 1), 90)
        );
      }, 240);
    }, SHOW_DELAY);
  };

  const finish = () => {
    clearTimeout(showTimer.current);
    showTimer.current = undefined;
    if (!active.current) return; // never showed (fast load) → nothing to finish
    clearInterval(trickle.current);
    active.current = false;
    setProgress(100);
    fade.current = setTimeout(() => setProgress(0), 260);
  };

  // Trigger on route change; safety-complete if no loading event arrives.
  useEffect(() => {
    begin();
    clearTimeout(safety.current);
    safety.current = setTimeout(finish, 2000);
    // Fully reset on navigation so each route change starts a fresh cycle:
    // clear the show-delay timer + trickle and reset `active` (not just the
    // safety timeout). Reset progress to 0 directly rather than calling finish()
    // to avoid a spurious 100%→0% completion flash when navigating mid-cycle.
    return () => {
      clearTimeout(safety.current);
      clearTimeout(showTimer.current);
      clearInterval(trickle.current);
      clearTimeout(fade.current);
      showTimer.current = undefined;
      active.current = false;
      setProgress(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Accurate completion from the app's global loading signal.
  useEffect(() => {
    const onLoading = (e: any) => (e?.detail ? begin() : finish());
    subscribe("loading", onLoading);
    return () => {
      unsubscribe("loading", onLoading);
      clearTimeout(showTimer.current);
      clearInterval(trickle.current);
      clearTimeout(fade.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* The visual bar is aria-hidden (decorative). Announce navigation
          loading to assistive tech via this visually-hidden live region. */}
      <div
        role="status"
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {progress > 0 ? "Loading…" : ""}
      </div>
      <div
        aria-hidden
        className="fixed top-0 left-0 right-0 z-[9999] h-[2px] pointer-events-none"
      >
        <div
          className="h-full"
          style={{
            width: `${progress}%`,
            backgroundColor: ACCENT,
            boxShadow: `0 0 8px ${ACCENT}80`,
            opacity: progress === 0 ? 0 : 1,
            transition:
              progress >= 100
                ? "width 180ms ease-out"
                : progress === 0
                ? "opacity 260ms ease, width 0ms"
                : "width 240ms cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        />
      </div>
    </>
  );
};

export default RouteProgressBar;
